/**
 * KUROGANE — encabezado, cajón del menú, búsqueda predictiva y barra de anuncios.
 *
 * Cuatro Custom Elements independientes. Cada uno crea su AbortController en
 * connectedCallback y lo aborta en disconnectedCallback: sin fugas de listeners
 * y remontaje limpio en el editor de temas.
 *
 * Contrato en docs/ARCHITECTURE.md §8.
 */
(function () {
  'use strict';

  const kg = window.kg;
  if (!kg) return;

  /* ================================================================== *
   * Utilidades locales
   * ================================================================== */
  const DESKTOP_QUERY = '(min-width: 990px)';
  const FINE_POINTER_QUERY = '(hover: hover) and (pointer: fine)';

  const isDesktop = () => window.matchMedia(DESKTOP_QUERY).matches;
  const isFinePointer = () => window.matchMedia(FINE_POINTER_QUERY).matches;

  function transitionDuration() {
    if (kg.prefersReducedMotion()) return 0;
    const raw = getComputedStyle(document.documentElement)
      .getPropertyValue('--kg-dur-base')
      .trim();
    const value = parseFloat(raw);
    if (Number.isNaN(value)) return 320;
    return raw.endsWith('ms') ? value : value * 1000;
  }

  function syncHeaderHeight() {
    const header = document.querySelector('[data-kg-header]');
    const height = header ? header.offsetHeight : 0;
    document.documentElement.style.setProperty('--kg-header-height', `${height}px`);
  }

  function escapeHtml(value) {
    const map = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
    return String(value == null ? '' : value).replace(/[&<>"']/g, (char) => map[char]);
  }

  function sizedImage(url, width) {
    if (!url) return '';
    return url.indexOf('?') === -1 ? `${url}?width=${width}` : `${url}&width=${width}`;
  }

  /* ================================================================== *
   * <kg-header>
   * ================================================================== */
  class KgHeader extends HTMLElement {
    connectedCallback() {
      this.abort = new AbortController();
      const { signal } = this.abort;

      this.headerEl = this.querySelector('[data-kg-header]');
      this.stickyMode = this.dataset.sticky || 'none';
      this.isTransparent = this.dataset.transparent === 'true';
      this.lastScrollY = Math.max(0, window.scrollY);
      this.openItem = null;
      this.hoverTimer = null;
      this.scrollTicking = false;

      this.drawer = this.querySelector('kg-menu-drawer');
      this.drawerToggle = this.querySelector('[data-drawer-toggle]');
      this.searchPanel = this.querySelector('[data-search-panel]');
      this.searchToggle = this.querySelector('[data-search-toggle]');
      this.cartLink = this.querySelector('[data-cart-link]');
      this.cartCount = this.querySelector('[data-cart-count]');

      this.bindNav(signal);
      this.bindDrawer(signal);
      this.bindSearch(signal);
      this.bindCart(signal);
      this.bindLocalization(signal);

      window.addEventListener('scroll', () => this.onScroll(), { signal, passive: true });
      window.addEventListener('resize', kg.throttle(() => this.onResize(), 150), { signal });

      document.addEventListener(
        'keydown',
        (event) => {
          if (event.key !== 'Escape') return;
          if (this.openItem) this.closeNavPanel(this.openItem, true);
        },
        { signal }
      );

      // Editor de temas: al seleccionar un bloque de mega menú, abrimos su panel.
      document.addEventListener('shopify:block:select', (event) => this.onBlockSelect(event), { signal });
      document.addEventListener('shopify:block:deselect', () => this.closeAllNavPanels(), { signal });

      this.updateScrollState(true);
      syncHeaderHeight();
    }

    disconnectedCallback() {
      if (this.abort) this.abort.abort();
      window.clearTimeout(this.hoverTimer);
    }

    /* ---------------- Navegación de escritorio ---------------- */
    bindNav(signal) {
      this.navItems = Array.from(this.querySelectorAll('[data-nav-item]'));

      this.navItems.forEach((item) => {
        const toggle = item.querySelector('[data-nav-toggle]');
        const panel = item.querySelector('[data-nav-panel]');
        if (!toggle || !panel) return;

        // Enter y Espacio ya disparan click en un <button>.
        toggle.addEventListener(
          'click',
          (event) => {
            event.preventDefault();
            if (item === this.openItem) this.closeNavPanel(item, true);
            else this.openNavPanel(item);
          },
          { signal }
        );

        toggle.addEventListener(
          'keydown',
          (event) => {
            if (event.key !== 'ArrowDown') return;
            event.preventDefault();
            this.openNavPanel(item);
            const first = kg.getFocusable(panel)[0];
            if (first) first.focus();
          },
          { signal }
        );

        item.addEventListener(
          'mouseenter',
          () => {
            if (!isDesktop() || !isFinePointer()) return;
            window.clearTimeout(this.hoverTimer);
            this.openNavPanel(item);
          },
          { signal }
        );

        item.addEventListener(
          'mouseleave',
          () => {
            if (!isDesktop() || !isFinePointer()) return;
            window.clearTimeout(this.hoverTimer);
            this.hoverTimer = window.setTimeout(() => this.closeNavPanel(item, false), 160);
          },
          { signal }
        );

        // Si el foco sale del elemento, el panel se cierra.
        item.addEventListener(
          'focusout',
          (event) => {
            if (event.relatedTarget && item.contains(event.relatedTarget)) return;
            this.closeNavPanel(item, false);
          },
          { signal }
        );
      });
    }

    openNavPanel(item) {
      if (this.openItem && this.openItem !== item) this.closeNavPanel(this.openItem, false);
      const toggle = item.querySelector('[data-nav-toggle]');
      item.dataset.open = 'true';
      if (toggle) toggle.setAttribute('aria-expanded', 'true');
      this.openItem = item;
      this.updateSolidState();
    }

    closeNavPanel(item, restoreFocus) {
      if (!item) return;
      const toggle = item.querySelector('[data-nav-toggle]');
      delete item.dataset.open;
      if (toggle) {
        toggle.setAttribute('aria-expanded', 'false');
        if (restoreFocus) toggle.focus();
      }
      if (this.openItem === item) this.openItem = null;
      this.updateSolidState();
    }

    closeAllNavPanels() {
      (this.navItems || []).forEach((item) => this.closeNavPanel(item, false));
    }

    onBlockSelect(event) {
      const blockId = event.detail && event.detail.blockId;
      if (!blockId) return;
      const panel = Array.from(this.querySelectorAll('[data-nav-panel][data-block-id]')).find(
        (el) => el.dataset.blockId === blockId
      );
      if (!panel) return;
      const item = panel.closest('[data-nav-item]');
      if (item) this.openNavPanel(item);
    }

    /* ---------------- Cajón del menú ---------------- */
    bindDrawer(signal) {
      if (!this.drawerToggle || !this.drawer) return;

      this.drawerToggle.addEventListener(
        'click',
        () => {
          if (this.drawer.isOpen) this.drawer.close();
          else this.drawer.open(this.drawerToggle);
        },
        { signal }
      );

      this.drawer.addEventListener(
        'kg:drawer:toggle',
        (event) => {
          this.drawerToggle.setAttribute('aria-expanded', String(event.detail.open));
          this.updateSolidState();
        },
        { signal }
      );
    }

    /* ---------------- Búsqueda ---------------- */
    bindSearch(signal) {
      if (!this.searchToggle || !this.searchPanel) return;

      this.searchToggle.addEventListener(
        'click',
        () => {
          if (this.searchPanel.dataset.open === 'true') this.searchPanel.close();
          else this.searchPanel.open(this.searchToggle);
        },
        { signal }
      );

      this.searchPanel.addEventListener(
        'kg:search:toggle',
        (event) => {
          this.searchToggle.setAttribute('aria-expanded', String(event.detail.open));
          this.updateSolidState();
        },
        { signal }
      );
    }

    /* ---------------- Carrito ---------------- */
    bindCart(signal) {
      document.addEventListener(
        kg.CART_EVENT,
        (event) => {
          const cart = event.detail && event.detail.cart;
          if (!cart) return;
          this.updateCartCount(cart.item_count);
        },
        { signal }
      );

      if (!this.cartLink) return;

      this.cartLink.addEventListener(
        'click',
        (event) => {
          const cartType = (window.kgTheme && window.kgTheme.cartType) || this.dataset.cartType;
          if (cartType !== 'drawer') return; // Sin panel: navegación normal a routes.cart
          if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
          event.preventDefault();
          document.dispatchEvent(new CustomEvent('kg:cart:open'));
        },
        { signal }
      );
    }

    updateCartCount(count) {
      const total = Number(count) || 0;

      if (this.cartCount) {
        this.cartCount.textContent = String(total);
        this.cartCount.hidden = total === 0;
      }

      const template = (window.kgTheme && window.kgTheme.strings && window.kgTheme.strings.cartCount) || '';
      if (this.cartLink && template) {
        this.cartLink.setAttribute('aria-label', template.replace('[count]', String(total)));
      }
    }

    /* ---------------- País e idioma (mejora progresiva) ---------------- */
    bindLocalization(signal) {
      // Delegado en document para cubrir también el selector del pie.
      document.addEventListener(
        'change',
        (event) => {
          const select = event.target.closest && event.target.closest('[data-localization-select]');
          if (!select || !select.form) return;
          if (typeof select.form.requestSubmit === 'function') select.form.requestSubmit();
          else select.form.submit();
        },
        { signal }
      );
    }

    /* ---------------- Scroll, sticky y transparencia ---------------- */
    onScroll() {
      if (this.scrollTicking) return;
      this.scrollTicking = true;
      window.requestAnimationFrame(() => {
        this.updateScrollState(false);
        this.scrollTicking = false;
      });
    }

    onResize() {
      syncHeaderHeight();
      if (!isDesktop()) this.closeAllNavPanels();
    }

    hasOpenOverlay() {
      return Boolean(
        this.openItem ||
          (this.drawer && this.drawer.isOpen) ||
          (this.searchPanel && this.searchPanel.dataset.open === 'true')
      );
    }

    updateScrollState(initial) {
      const y = Math.max(0, window.scrollY);
      const headerHeight = (this.headerEl && this.headerEl.offsetHeight) || 1;
      const scrolled = y > headerHeight * 0.5;

      this.classList.toggle('is-scrolled', scrolled);
      this.classList.toggle('is-stuck', scrolled && this.stickyMode !== 'none');

      if (this.stickyMode === 'on-scroll-up' && !initial) {
        const goingDown = y > this.lastScrollY;
        const pastThreshold = y > headerHeight * 2;
        this.classList.toggle('is-hidden', goingDown && pastThreshold && !this.hasOpenOverlay());
      } else {
        this.classList.remove('is-hidden');
      }

      this.lastScrollY = y;
      this.updateSolidState();
    }

    updateSolidState() {
      if (!this.headerEl) return;
      if (this.hasOpenOverlay()) this.classList.remove('is-hidden');
      if (!this.isTransparent) return;
      const solid = this.classList.contains('is-scrolled') || this.hasOpenOverlay();
      this.headerEl.classList.toggle('is-solid', solid);
    }
  }
  if (!customElements.get('kg-header')) customElements.define('kg-header', KgHeader);

  /* ================================================================== *
   * <kg-menu-drawer> — navegación móvil por paneles
   * ================================================================== */
  class KgMenuDrawer extends HTMLElement {
    connectedCallback() {
      this.abort = new AbortController();
      const { signal } = this.abort;

      this.panels = Array.from(this.querySelectorAll('[data-panel]'));
      this.titleEl = this.querySelector('[data-drawer-title]');
      this.defaultTitle = this.titleEl ? this.titleEl.textContent : '';
      this.rootPanelId = this.dataset.rootPanel || (this.panels[0] && this.panels[0].id);
      this.activePanelId = this.rootPanelId;
      this.history = [];

      this.querySelectorAll('[data-drawer-close]').forEach((btn) => {
        btn.addEventListener('click', () => this.close(), { signal });
      });

      this.querySelectorAll('[data-panel-open]').forEach((btn) => {
        btn.addEventListener('click', () => this.goTo(btn.dataset.panelOpen, btn), { signal });
      });

      this.querySelectorAll('[data-panel-back]').forEach((btn) => {
        btn.addEventListener('click', () => this.goBack(btn.dataset.panelBack), { signal });
      });

      document.addEventListener(
        'keydown',
        (event) => {
          if (event.key === 'Escape' && this.isOpen) this.close();
        },
        { signal }
      );
    }

    disconnectedCallback() {
      if (this.abort) this.abort.abort();
      this.panels.forEach((panel) => window.clearTimeout(panel._kgHideTimer));
      if (this.isOpen) {
        kg.releaseFocus(this);
        kg.unlockScroll(true);
        kg.hideOverlay();
        delete this.dataset.open;
      }
    }

    get isOpen() {
      return this.dataset.open === 'true';
    }

    open(trigger) {
      if (this.isOpen) return;
      this.opener = trigger || document.activeElement;
      this.dataset.open = 'true';
      kg.lockScroll();
      kg.showOverlay(() => this.close());
      kg.trapFocus(this);
      this.emitToggle(true);
    }

    close() {
      if (!this.isOpen) return;
      delete this.dataset.open;
      kg.unlockScroll();
      kg.hideOverlay();
      kg.releaseFocus(this);
      if (this.opener && typeof this.opener.focus === 'function') {
        this.opener.focus({ preventScroll: true });
      }
      this.emitToggle(false);
      window.setTimeout(() => this.reset(), transitionDuration() + 60);
    }

    emitToggle(open) {
      this.dispatchEvent(new CustomEvent('kg:drawer:toggle', { bubbles: true, detail: { open } }));
    }

    panelById(id) {
      return this.panels.find((panel) => panel.id === id) || null;
    }

    goTo(panelId, trigger) {
      const next = this.panelById(panelId);
      const current = this.panelById(this.activePanelId);
      if (!next || next === current) return;

      if (trigger) trigger.setAttribute('aria-expanded', 'true');
      this.history.push({ id: this.activePanelId, trigger: trigger || null });

      this.showPanel(next, current, 'forward');
      this.activePanelId = panelId;
      this.updateTitle(next);

      const target = next.querySelector('[data-panel-back]') || kg.getFocusable(next)[0];
      if (target) window.requestAnimationFrame(() => target.focus({ preventScroll: true }));
    }

    goBack(fallbackId) {
      const previous = this.history.pop();
      const targetId = (previous && previous.id) || fallbackId || this.rootPanelId;
      const target = this.panelById(targetId);
      const current = this.panelById(this.activePanelId);
      if (!target) return;

      this.showPanel(target, current, 'back');
      this.activePanelId = targetId;
      this.updateTitle(target);

      if (previous && previous.trigger) {
        previous.trigger.setAttribute('aria-expanded', 'false');
        window.requestAnimationFrame(() => previous.trigger.focus({ preventScroll: true }));
      }
    }

    showPanel(next, current, direction) {
      window.clearTimeout(next._kgHideTimer);
      next.hidden = false;
      void next.offsetWidth; // reflow: la transición necesita un estado inicial pintado
      next.dataset.state = 'active';
      next.removeAttribute('aria-hidden');

      if (!current || current === next) return;

      current.dataset.state = direction === 'forward' ? 'behind' : 'ahead';
      current.setAttribute('aria-hidden', 'true');
      window.clearTimeout(current._kgHideTimer);
      current._kgHideTimer = window.setTimeout(() => {
        if (current.dataset.state !== 'active') current.hidden = true;
      }, transitionDuration() + 60);
    }

    updateTitle(panel) {
      if (!this.titleEl) return;
      this.titleEl.textContent = panel.dataset.panelTitle || this.defaultTitle;
    }

    reset() {
      if (this.isOpen) return;
      this.history = [];
      this.activePanelId = this.rootPanelId;

      this.panels.forEach((panel) => {
        window.clearTimeout(panel._kgHideTimer);
        const isRoot = panel.id === this.rootPanelId;
        panel.hidden = !isRoot;
        panel.dataset.state = isRoot ? 'active' : 'ahead';
        if (isRoot) panel.removeAttribute('aria-hidden');
        else panel.setAttribute('aria-hidden', 'true');
      });

      this.querySelectorAll('[data-panel-open]').forEach((btn) => {
        btn.setAttribute('aria-expanded', 'false');
      });

      if (this.titleEl) this.titleEl.textContent = this.defaultTitle;
    }
  }
  if (!customElements.get('kg-menu-drawer')) customElements.define('kg-menu-drawer', KgMenuDrawer);

  /* ================================================================== *
   * <kg-predictive-search>
   * ================================================================== */
  class KgPredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.abort = new AbortController();
      const { signal } = this.abort;

      this.input = this.querySelector('[data-search-input]');
      this.form = this.querySelector('[data-search-form]');
      this.results = this.querySelector('[data-search-results]');
      this.status = this.querySelector('[data-search-status]');
      this.resetBtn = this.querySelector('[data-search-reset]');

      this.predictive = this.dataset.predictive === 'true';
      this.types = (this.dataset.types || '').split(',').filter(Boolean);
      this.limit = parseInt(this.dataset.limit, 10) || 4;
      this.strings = this.parseStrings();
      this.options = [];
      this.activeIndex = -1;
      this.debouncedSearch = kg.debounce(() => this.search(), 300);

      this.querySelectorAll('[data-search-close]').forEach((btn) => {
        btn.addEventListener('click', () => this.close(), { signal });
      });

      document.addEventListener(
        'keydown',
        (event) => {
          if (event.key === 'Escape' && this.dataset.open === 'true') this.close();
        },
        { signal }
      );

      if (this.resetBtn) {
        this.resetBtn.addEventListener('click', () => this.clear(), { signal });
      }

      if (!this.input) return;

      this.input.addEventListener('input', () => this.onInput(), { signal });

      if (this.results && this.predictive) {
        this.results.setAttribute('role', 'listbox');
        this.results.setAttribute('aria-label', this.strings.listboxLabel || '');
        this.input.addEventListener('keydown', (event) => this.onKeydown(event), { signal });
      }
    }

    disconnectedCallback() {
      if (this.abort) this.abort.abort();
      if (this.controller) this.controller.abort();
      if (this.dataset.open === 'true') {
        kg.releaseFocus(this);
        kg.unlockScroll(true);
        kg.hideOverlay();
        delete this.dataset.open;
      }
    }

    parseStrings() {
      try {
        return JSON.parse(this.dataset.strings || '{}');
      } catch (error) {
        return {};
      }
    }

    /* ---------------- Apertura y cierre ---------------- */
    open(trigger) {
      if (this.dataset.open === 'true') return;
      this.opener = trigger || document.activeElement;
      this.dataset.open = 'true';
      kg.lockScroll();
      kg.showOverlay(() => this.close());
      kg.trapFocus(this, this.input || undefined);
      this.dispatchEvent(new CustomEvent('kg:search:toggle', { bubbles: true, detail: { open: true } }));
    }

    close() {
      if (this.dataset.open !== 'true') return;
      delete this.dataset.open;
      kg.unlockScroll();
      kg.hideOverlay();
      kg.releaseFocus(this);
      if (this.opener && typeof this.opener.focus === 'function') {
        this.opener.focus({ preventScroll: true });
      }
      this.dispatchEvent(new CustomEvent('kg:search:toggle', { bubbles: true, detail: { open: false } }));
    }

    /* ---------------- Entrada ---------------- */
    onInput() {
      const term = this.input.value.trim();
      if (this.resetBtn) this.resetBtn.hidden = term.length === 0;
      if (!this.predictive) return;

      if (term.length < 2) {
        if (this.controller) this.controller.abort();
        this.clearResults();
        return;
      }

      this.setStatus(this.strings.searching || '');
      this.debouncedSearch();
    }

    clear() {
      if (this.input) {
        this.input.value = '';
        this.input.focus();
      }
      if (this.resetBtn) this.resetBtn.hidden = true;
      if (this.controller) this.controller.abort();
      this.clearResults();
    }

    onKeydown(event) {
      const count = this.options.length;

      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        if (!count) return;
        event.preventDefault();
        const direction = event.key === 'ArrowDown' ? 1 : -1;
        let next = this.activeIndex + direction;
        if (next < 0) next = count - 1;
        if (next >= count) next = 0;
        this.setActive(next);
        return;
      }

      if (event.key === 'Enter') {
        if (this.activeIndex < 0) return; // Sin selección: se envía el formulario
        const link = this.options[this.activeIndex].querySelector('a[href]');
        if (!link) return;
        event.preventDefault();
        window.location.href = link.href;
        return;
      }

      if (event.key === 'Escape' && this.input.value) {
        event.stopPropagation();
        this.clear();
      }
    }

    setActive(index) {
      this.options.forEach((option, i) => {
        const active = i === index;
        option.setAttribute('aria-selected', String(active));
        option.classList.toggle('is-active', active);
        if (active && typeof option.scrollIntoView === 'function') {
          option.scrollIntoView({ block: 'nearest' });
        }
      });

      this.activeIndex = index;
      if (index >= 0) this.input.setAttribute('aria-activedescendant', this.options[index].id);
      else this.input.removeAttribute('aria-activedescendant');
    }

    setStatus(message) {
      if (this.status) this.status.textContent = message || '';
    }

    clearResults() {
      if (!this.results) return;
      this.results.innerHTML = '';
      this.results.hidden = true;
      this.options = [];
      this.activeIndex = -1;
      this.input.setAttribute('aria-expanded', 'false');
      this.input.removeAttribute('aria-activedescendant');
      this.setStatus('');
    }

    /* ---------------- Consulta ---------------- */
    async search() {
      if (!this.input) return;
      const term = this.input.value.trim();
      if (term.length < 2) return;

      const routes = (window.kgTheme && window.kgTheme.routes) || {};
      const endpoint = routes.predictiveSearch;
      if (!endpoint || this.types.length === 0) return;

      if (this.controller) this.controller.abort();
      this.controller = new AbortController();

      const params = new URLSearchParams();
      params.set('q', term);
      params.set('resources[type]', this.types.join(','));
      params.set('resources[limit]', String(this.limit));
      params.set('resources[options][unavailable_products]', 'last');

      try {
        const data = await kg.fetchJSON(`${endpoint}?${params.toString()}`, {
          signal: this.controller.signal
        });
        const results = (data && data.resources && data.resources.results) || {};
        this.render(results, term);
      } catch (error) {
        if (error && error.name === 'AbortError') return;
        this.clearResults();
      }
    }

    /* ---------------- Render ---------------- */
    render(results, term) {
      if (!this.results) return;

      const groups = [
        { key: 'products', label: this.strings.products, items: results.products },
        { key: 'collections', label: this.strings.collections, items: results.collections },
        { key: 'articles', label: this.strings.articles, items: results.articles },
        { key: 'pages', label: this.strings.pages, items: results.pages },
        { key: 'queries', label: this.strings.queries, items: results.queries }
      ].filter((group) => Array.isArray(group.items) && group.items.length > 0);

      const total = groups.reduce((sum, group) => sum + group.items.length, 0);

      if (total === 0) {
        this.renderEmpty(term);
        return;
      }

      let index = 0;
      let html = '';

      groups.forEach((group) => {
        html += `<div class="kg-search-group" role="group" aria-label="${escapeHtml(group.label || '')}">`;
        html += `<p class="kg-search-group__title" aria-hidden="true">${escapeHtml(group.label || '')}</p>`;
        html += '<ul class="kg-search-group__list" role="none">';
        group.items.forEach((item) => {
          html += this.optionMarkup(group.key, item, index);
          index += 1;
        });
        html += '</ul></div>';
      });

      const viewAll = (this.strings.viewAll || '').replace('__COUNT__', String(total));
      if (viewAll && this.form) {
        const searchUrl = this.form.getAttribute('action') || '';
        const href = `${searchUrl}?q=${encodeURIComponent(term)}&options%5Bprefix%5D=last`;
        html += `<div class="kg-search-results__all"><a class="kg-btn kg-btn--link" href="${escapeHtml(href)}">${escapeHtml(viewAll)}</a></div>`;
      }

      this.results.innerHTML = html;
      this.results.hidden = false;
      this.options = Array.from(this.results.querySelectorAll('[role="option"]'));
      this.activeIndex = -1;
      this.input.setAttribute('aria-expanded', 'true');
      this.input.removeAttribute('aria-activedescendant');

      const countMessage = (this.strings.resultsCount || '').replace('__COUNT__', String(total));
      this.setStatus(countMessage);
    }

    renderEmpty(term) {
      const message = (this.strings.noResults || '').replace('__TERMS__', escapeHtml(term));
      this.results.innerHTML =
        `<div class="kg-search-empty">` +
        `<p class="kg-search-empty__title">${message}</p>` +
        `<p class="kg-search-empty__hint">${escapeHtml(this.strings.noResultsHint || '')}</p>` +
        `</div>`;
      this.results.hidden = false;
      this.options = [];
      this.activeIndex = -1;
      this.input.setAttribute('aria-expanded', 'false');
      this.input.removeAttribute('aria-activedescendant');
      this.setStatus(this.strings.noResultsHint || '');
    }

    optionMarkup(kind, item, index) {
      const id = `${this.id}-opt-${index}`;
      const url = escapeHtml(item.url || '');
      const title = escapeHtml(item.title || '');

      let media = '';
      if (item.image) {
        media =
          `<span class="kg-search-result__media kg-media">` +
          `<img src="${escapeHtml(sizedImage(item.image, 160))}" alt="" width="80" height="80" loading="lazy" decoding="async">` +
          `</span>`;
      }

      let meta = '';
      if (kind === 'products') {
        const cents = parseInt(item.price, 10);
        if (!Number.isNaN(cents)) {
          meta = `<span class="kg-search-result__price">${escapeHtml(kg.formatMoney(cents))}</span>`;
        }
      }

      return (
        `<li class="kg-search-result" role="option" id="${id}" aria-selected="false">` +
        `<a class="kg-search-result__link" href="${url}" tabindex="-1">` +
        media +
        `<span class="kg-search-result__body">` +
        `<span class="kg-search-result__title">${title}</span>` +
        meta +
        `</span>` +
        `</a>` +
        `</li>`
      );
    }
  }
  if (!customElements.get('kg-predictive-search')) {
    customElements.define('kg-predictive-search', KgPredictiveSearch);
  }

  /* ================================================================== *
   * <kg-announcement-bar>
   * ================================================================== */
  class KgAnnouncementBar extends HTMLElement {
    connectedCallback() {
      this.abort = new AbortController();
      const { signal } = this.abort;

      this.slides = Array.from(this.querySelectorAll('[data-announcement-slide]'));
      this.index = 0;
      this.timer = null;
      this.storageKey = this.dataset.storageKey || '';
      this.interval = parseInt(this.dataset.interval, 10) || 6000;

      this.shouldRotate =
        this.dataset.rotate === 'true' && this.slides.length > 1 && !kg.prefersReducedMotion();

      if (this.isDismissed()) {
        this.hidden = true;
        return;
      }

      const closeBtn = this.querySelector('[data-announcement-close]');
      if (closeBtn) closeBtn.addEventListener('click', () => this.dismiss(), { signal });

      const prevBtn = this.querySelector('[data-announcement-prev]');
      const nextBtn = this.querySelector('[data-announcement-next]');
      if (prevBtn) {
        prevBtn.addEventListener('click', () => { this.go(this.index - 1); this.restart(); }, { signal });
      }
      if (nextBtn) {
        nextBtn.addEventListener('click', () => { this.go(this.index + 1); this.restart(); }, { signal });
      }

      if (!this.shouldRotate) return;

      this.addEventListener('mouseenter', () => this.stop(), { signal });
      this.addEventListener('mouseleave', () => this.start(), { signal });
      this.addEventListener('focusin', () => this.stop(), { signal });
      this.addEventListener(
        'focusout',
        (event) => {
          if (event.relatedTarget && this.contains(event.relatedTarget)) return;
          this.start();
        },
        { signal }
      );
      document.addEventListener(
        'visibilitychange',
        () => (document.hidden ? this.stop() : this.start()),
        { signal }
      );

      this.start();
    }

    disconnectedCallback() {
      if (this.abort) this.abort.abort();
      this.stop();
    }

    isDismissed() {
      if (this.dataset.dismissible !== 'true' || !this.storageKey) return false;
      try {
        return window.sessionStorage.getItem(this.storageKey) === 'closed';
      } catch (error) {
        return false;
      }
    }

    dismiss() {
      this.stop();
      this.hidden = true;
      try {
        if (this.storageKey) window.sessionStorage.setItem(this.storageKey, 'closed');
      } catch (error) {
        /* Modo privado: no persistimos, pero la barra ya está cerrada. */
      }
      syncHeaderHeight();
    }

    go(next) {
      const total = this.slides.length;
      if (total === 0) return;
      const target = ((next % total) + total) % total;

      this.slides.forEach((slide, i) => {
        slide.hidden = i !== target;
      });

      this.index = target;
    }

    start() {
      if (!this.shouldRotate || this.timer) return;
      this.timer = window.setInterval(() => this.go(this.index + 1), this.interval);
    }

    stop() {
      if (!this.timer) return;
      window.clearInterval(this.timer);
      this.timer = null;
    }

    restart() {
      this.stop();
      this.start();
    }
  }
  if (!customElements.get('kg-announcement-bar')) {
    customElements.define('kg-announcement-bar', KgAnnouncementBar);
  }

  /* ================================================================== *
   * Editor de temas: reseteo de estado global compartido.
   * Los Custom Elements se remontan solos; el scroll bloqueado y el overlay
   * son globales y hay que devolverlos a su sitio.
   * ================================================================== */
  if (!window.__kgHeaderEditorBound) {
    window.__kgHeaderEditorBound = true;

    document.addEventListener('shopify:section:load', () => {
      syncHeaderHeight();
      const open = document.querySelector(
        'kg-menu-drawer[data-open="true"], [data-search-panel][data-open="true"]'
      );
      if (!open) {
        kg.unlockScroll(true);
        kg.hideOverlay();
      }
    });

    document.addEventListener('shopify:section:unload', () => {
      const open = document.querySelector(
        'kg-menu-drawer[data-open="true"], [data-search-panel][data-open="true"]'
      );
      if (!open) {
        kg.unlockScroll(true);
        kg.hideOverlay();
      }
    });
  }
})();
